import { useCallback, useEffect, useRef } from 'react';
import { useSetRecoilState } from 'recoil';
import { blockBelowDataTailHeightState, blockTrailerHeightState } from 'venn-state';

/**
 * `tailPx` is the local-space gap between the content-bottom element (`trailerEl`, or the grid's
 * `.ag-root-wrapper` when there is no trailer) and `.ag-center-cols-container`. Both elements share
 * the continuation wrapper's `translateY` and the report page's `scale(zoom)` ancestor transforms,
 * so `translateY` cancels out of the `getBoundingClientRect().bottom` subtraction while `scale`
 * does not — dividing by `scale` converts the remainder back to local, unscaled pixels.
 */
export const measureTrailerAndTail = (
  blockEl: HTMLElement | null,
  trailerEl: HTMLElement | null,
  scale: number,
): { trailerPx: number; tailPx: number } => {
  const trailerPx = trailerEl?.offsetHeight ?? 0;
  const contentBottomEl = trailerEl ?? blockEl?.querySelector<HTMLElement>('.ag-root-wrapper') ?? null;
  const agCenterCols = blockEl?.querySelector<HTMLElement>('.ag-center-cols-container') ?? null;
  const safeScale = scale > 0 ? scale : 1;
  const tailPx =
    contentBottomEl && agCenterCols
      ? Math.max(
          0,
          (contentBottomEl.getBoundingClientRect().bottom - agCenterCols.getBoundingClientRect().bottom) / safeScale,
        )
      : 0;
  return { trailerPx, tailPx };
};

/**
 * Keeps `blockTrailerHeightState` / `blockBelowDataTailHeightState` fresh for a continuation
 * instance. The source page's `ReportLabBlockContainer` stops measuring once react-virtuoso
 * unmounts it, so a continuation whose trailer changes shape (e.g. a toggled-on footer section)
 * needs its own measurement path.
 */
export const useContinuationTrailerMeasurement = (
  blockId: string,
  { enabled = true }: { enabled?: boolean } = {},
): { blockRef: React.RefObject<HTMLDivElement>; setTrailerElement: (element: HTMLElement | null) => void } => {
  const blockRef = useRef<HTMLDivElement>(null);
  const trailerRef = useRef<HTMLElement | null>(null);
  const resizeObserverRef = useRef<ResizeObserver | null>(null);
  const measurementFrameRef = useRef<number | null>(null);

  const setTrailerHeight = useSetRecoilState(blockTrailerHeightState(blockId));
  const setBelowDataTailHeight = useSetRecoilState(blockBelowDataTailHeightState(blockId));

  // A slice instance that never mounts the real trailer (e.g. a non-last virtualized window) would
  // otherwise measure a false "no trailer" reading on mount and overwrite this shared-by-blockId
  // state out from under whichever instance IS authoritative. `enabled: false` makes it inert.
  const measure = useCallback(() => {
    if (!enabled) return;
    const el = blockRef.current;
    const scale = el && el.offsetWidth > 0 ? el.getBoundingClientRect().width / el.offsetWidth : 1;
    const { trailerPx, tailPx } = measureTrailerAndTail(el, trailerRef.current, scale);
    setTrailerHeight(trailerPx);
    setBelowDataTailHeight(tailPx);
  }, [enabled, setBelowDataTailHeight, setTrailerHeight]);

  const scheduleMeasure = useCallback(() => {
    if (measurementFrameRef.current != null) return;

    measurementFrameRef.current = requestAnimationFrame(() => {
      measurementFrameRef.current = null;
      measure();
    });
  }, [measure]);

  const setTrailerElement = useCallback(
    (element: HTMLElement | null) => {
      if (trailerRef.current === element) return;

      if (trailerRef.current) {
        resizeObserverRef.current?.unobserve(trailerRef.current);
      }
      trailerRef.current = element;
      if (element) {
        resizeObserverRef.current?.observe(element);
      }
      scheduleMeasure();
    },
    [scheduleMeasure],
  );

  useEffect(() => {
    if (!enabled) return undefined;

    if (typeof ResizeObserver === 'undefined') {
      scheduleMeasure();
      return undefined;
    }

    const resizeObserver = new ResizeObserver(() => {
      scheduleMeasure();
    });
    resizeObserverRef.current = resizeObserver;
    if (trailerRef.current) {
      resizeObserver.observe(trailerRef.current);
    }
    scheduleMeasure();

    return () => {
      resizeObserver.disconnect();
      resizeObserverRef.current = null;
      if (measurementFrameRef.current != null) {
        cancelAnimationFrame(measurementFrameRef.current);
      }
      measurementFrameRef.current = null;
    };
  }, [enabled, scheduleMeasure]);

  return { blockRef, setTrailerElement };
};
